import type { ArticleDetail, ArticleSummary, CategorySummary, IngestionLogSummary, PaginatedArticles, SourceSummary, TopicSummary, TrendingTopic } from '@ai-radar/shared';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export class ApiRequestError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers }
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message = typeof payload === 'object' && payload !== null && 'error' in payload && typeof payload.error === 'string' ? payload.error : `Request failed (${response.status})`;
    throw new ApiRequestError(response.status, message);
  }
  return payload as T;
};

const queryString = (params: Record<string, string | number | undefined | null>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  const result = query.toString();
  return result ? `?${result}` : '';
};

export const api = {
  articles: (params: Record<string, string | number | undefined | null> = {}, signal?: AbortSignal) => request<PaginatedArticles>(`/api/articles${queryString(params)}`, { signal }),
  search: (q: string, params: Record<string, string | number | undefined | null> = {}, signal?: AbortSignal) => request<PaginatedArticles>(`/api/search${queryString({ ...params, q })}`, { signal }),
  article: (id: string, signal?: AbortSignal) => request<ArticleDetail>(`/api/articles/${encodeURIComponent(id)}`, { signal }),
  topStories: (signal?: AbortSignal) => request<ArticleSummary[]>('/api/top-stories', { signal }),
  sources: (signal?: AbortSignal) => request<SourceSummary[]>('/api/sources', { signal }),
  categories: (signal?: AbortSignal) => request<CategorySummary[]>('/api/categories', { signal }),
  topics: (signal?: AbortSignal) => request<TopicSummary[]>('/api/topics?limit=30', { signal }),
  trending: (signal?: AbortSignal) => request<TrendingTopic[]>('/api/trending', { signal }),
  health: (signal?: AbortSignal) => request<{ ok: boolean; service: string; timestamp: string }>('/api/health', { signal }),
  adminSources: (key: string) => request<SourceSummary[]>('/api/admin/sources', { headers: { 'x-admin-key': key } }),
  adminLogs: (key: string) => request<IngestionLogSummary[]>('/api/admin/ingestion-logs?limit=80', { headers: { 'x-admin-key': key } }),
  adminHealth: (key: string) => request<{ ingestionRunning: boolean; latestLog: { status: string; source: string | null; startedAt: string } | null }>('/api/admin/health', { headers: { 'x-admin-key': key } }),
  adminSettings: (key: string) => request<{ ingestionIntervalMinutes: number }>('/api/admin/settings', { headers: { 'x-admin-key': key } }),
  updateSource: (key: string, id: string, data: { active?: boolean; priority?: number }) => request<SourceSummary>(`/api/admin/sources/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'x-admin-key': key }, body: JSON.stringify(data) }),
  triggerIngestion: (key: string, sourceSlug?: string) => request<{ results: unknown[] }>('/api/admin/ingest', { method: 'POST', headers: { 'x-admin-key': key }, body: JSON.stringify(sourceSlug ? { sourceSlug } : {}) }),
  updateSettings: (key: string, ingestionIntervalMinutes: number) => request<{ ingestionIntervalMinutes: number }>('/api/admin/settings', { method: 'PATCH', headers: { 'x-admin-key': key }, body: JSON.stringify({ ingestionIntervalMinutes }) })
};
